// Persists a purchase intent in sessionStorage

/**
 * A purchase the user tried to start while signed out. We persist it in
 * sessionStorage so it survives the full-page navigations that punctuate auth
 * (Twitch OAuth round-trip, login → library redirect). After the user signs in,
 * `useAuthPostLogin` consumes the intent and opens a Stripe Checkout session.
 *
 * Writer: `useProductCheckout` when a signed-out user clicks buy.
 * Reader: `useAuthPostLogin` after the user becomes authenticated.
 */

const SESSION_KEY = 'purchaseIntent';

export type PurchaseIntent = {
  stripePriceId: string;
  checkoutAttemptId?: string;
};

export function setPurchaseIntent(intent: PurchaseIntent): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(intent));
}

export function consumePurchaseIntent(): PurchaseIntent | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) {
    return null;
  }

  sessionStorage.removeItem(SESSION_KEY);

  try {
    const parsed = JSON.parse(raw) as PurchaseIntent;
    if (typeof parsed?.stripePriceId === 'string' && parsed.stripePriceId) {
      return parsed;
    }
  } catch {
    // Malformed payload — treat as no intent.
  }

  return null;
}
